/**
 * Supply Chain Certification Services page content
 * (/supply-chain-certification-services).
 *
 * Every string in this file is transcribed verbatim from the approved
 * "Supply Chain Certifications Services (Main Page) — SEO Optimized 2026"
 * requirement document (MBA_MERN_Solutions/Supply Chain Certifications.docx).
 * Do not reword, shorten or extend this copy — update the document first.
 *
 * ON THE URL: the document suggests /supply-chain-certifications. The route
 * built is /supply-chain-certification-services, as specified for this task and
 * as the Services page links to it. Only one route exists for this content —
 * no duplicate URL was added.
 *
 * NOTE the document also carries a per-service SEO Title and Meta Description
 * for each of the two services. Those are page metadata for future dedicated
 * sub-pages, not body copy, so they are NOT rendered here — only this page's
 * own title and description are used, from `supplyChainSeo` below.
 *
 * FSC CoC carries a third list the other service does not ("Industries Using
 * FSC CoC:"), passed through as `extraLabel`/`extraItems`.
 *
 * `logoKey` values resolve against src/data/certificationLogos.js — both
 * services have a real mark. `image` paths point at the illustrations already
 * in public/assets/certification-process. No new artwork was introduced.
 */

export const supplyChainSeo = {
  title:
    "Supply Chain Certification Consultancy | FSC Chain of Custody & PEFC Certification | MBA Solutions",
  description:
    "MBA Solutions provides supply chain certification consultancy including FSC Chain of Custody and PEFC certification support to help businesses achieve responsible sourcing and sustainability goals.",
  slug: "/supply-chain-certification-services",
};

export const heroContent = {
  h1: "Building Transparent & Sustainable Supply Chains",
  subtitle: "Helping Businesses Achieve Responsible Sourcing Standards",
  paragraphs: [
    "Modern supply chains require transparency, traceability and responsible sourcing practices to meet global market expectations.",
    "MBA Solutions helps organizations implement internationally recognized supply chain certification systems that demonstrate responsible material sourcing, sustainability commitment and supply chain control.",
    "Our experts support businesses through assessment, documentation, implementation and certification preparation.",
  ],
};

export const servicesContent = {
  title: "Our Supply Chain Certification Services",
  standards: [
    {
      id: "fsc-coc",
      logoKey: "fsc-coc",
      title: "1. FSC Chain of Custody (FSC CoC)",
      headline:
        "FSC Chain of Custody Certification – Ensuring Responsible Material Traceability",
      paragraphs: [
        "FSC Chain of Custody (FSC CoC) certification helps organizations demonstrate that forest-based materials are sourced from responsibly managed forests and controlled supply chains.",
        "It provides a transparent system for tracking FSC-certified materials throughout the supply chain — from sourcing to final product.",
        "MBA Solutions helps businesses establish FSC CoC systems, prepare documentation and achieve certification readiness.",
      ],
      listLabel: "Our FSC CoC Consultancy Includes:",
      items: [
        "FSC requirement assessment",
        "Supply chain evaluation",
        "Material flow analysis",
        "Documentation development",
        "Traceability system support",
        "Internal procedure preparation",
        "Audit preparation",
      ],
      extraLabel: "Industries Using FSC CoC:",
      extraItems: [
        "Paper & Packaging",
        "Textile & Apparel",
        "Furniture Industry",
        "Printing Businesses",
        "Manufacturing Organizations",
        "Export Businesses",
      ],
      benefitsLabel: "Benefits of FSC Chain of Custody Certification:",
      benefits: [
        "Demonstrates responsible sourcing",
        "Improves supply chain transparency",
        "Supports sustainability commitments",
        "Meets global buyer requirements",
        "Builds customer trust",
      ],
    },
    {
      id: "pefc",
      logoKey: "pefc",
      title: "2. PEFC Certification",
      headline:
        "PEFC Certification – Supporting Sustainable Forest-Based Supply Chains",
      paragraphs: [
        "PEFC (Programme for the Endorsement of Forest Certification) promotes responsible forest management and sustainable use of forest resources.",
        "PEFC certification helps organizations demonstrate that their materials come from responsibly managed sources and controlled supply chains.",
        "MBA Solutions provides consultancy support to help businesses understand requirements, develop systems and prepare for certification.",
      ],
      listLabel: "Our PEFC Consultancy Includes:",
      items: [
        "PEFC requirement review",
        "Supply chain assessment",
        "Documentation support",
        "Process improvement",
        "Traceability system development",
        "Certification preparation",
      ],
      benefitsLabel: "Benefits of PEFC Certification:",
      benefits: [
        "Supports sustainable sourcing",
        "Improves environmental responsibility",
        "Enhances brand credibility",
        "Meets customer sustainability expectations",
        "Strengthens supply chain management",
      ],
    },
  ],
};

export const processContent = {
  title: "Our Supply Chain Certification Process",
  steps: [
    {
      number: "1",
      image: "/assets/certification-process/consultation.webp",
      title: "Step 1: Initial Consultation",
      description:
        "Understanding your products, supply chain and certification requirements.",
    },
    {
      number: "2",
      image: "/assets/certification-process/gap-assessment.webp",
      title: "Step 2: Gap Assessment",
      description:
        "Reviewing existing practices against certification requirements.",
    },
    {
      number: "3",
      image: "/assets/certification-process/documentation.webp",
      title: "Step 3: Documentation Development",
      description: "Preparing policies, procedures and required records.",
    },
    {
      number: "4",
      image: "/assets/certification-process/implementation.webp",
      title: "Step 4: Implementation Support",
      description:
        "Helping your team apply certification requirements effectively.",
    },
    {
      number: "5",
      image: "/assets/certification-process/internal-audit.webp",
      title: "Step 5: Internal Review",
      description: "Checking system readiness before certification assessment.",
    },
    {
      number: "6",
      image: "/assets/certification-process/certification-support.webp",
      title: "Step 6: Certification Support",
      description:
        "Providing guidance throughout the certification audit process.",
    },
  ],
};

export const whyChooseContent = {
  title: "Why Choose MBA Solutions?",
  reasons: [
    "Supply Chain Compliance Expertise",
    "Sustainability-Focused Solutions",
    "International Standards Knowledge",
    "Practical Implementation Support",
    "Complete Certification Guidance",
  ],
};

export const finalCtaContent = {
  title: "Build A Responsible & Transparent Supply Chain",
  paragraph:
    "Partner with MBA Solutions to achieve supply chain certifications and strengthen your sustainability performance.",
  cta: {
    label: "Get Supply Chain Certification Consultation Today",
    to: "/contact",
  },
};
